import React, { useState, useEffect, useCallback } from 'react';
import {
  Globe, Plus, Download, RefreshCw, X, Sparkles,
} from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import HolidayCard from '../../components/holidays/HolidayCard';
import CountryFlag from '../../components/common/CountryFlag';
import './Holidays.css';

function Holidays() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [populateLoading, setPopulateLoading] = useState(false);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [maxObserved, setMaxObserved] = useState(9); // default, fetched from server

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    recurring_type: 'fixed',
    fixed_month: '',
    fixed_day: '',
    variable_rule: '',
    is_observed: true,
    is_recurring: true,
  });

  const fetchCountries = useCallback(async () => {
    try {
      const res = await api.get('/countries');
      let countriesList = res.countries || [];

      // Managers: filter to only countries they're assigned to manage
      if (!isAdmin) {
        try {
          const hcRes = await api.get('/users/me/holiday-countries');
          const assignedIds = new Set((hcRes.holiday_countries || []).map(hc => hc.country_id));
          countriesList = countriesList.filter(c => assignedIds.has(c.id));
        } catch {
          countriesList = [];
        }
      }

      setCountries(countriesList);
      if (countriesList.length > 0) {
        setSelectedCountry(prev => prev || countriesList[0]);
      }
    } catch (err) {
      setCountries([]);
    }
  }, [isAdmin]);

  const fetchHolidays = useCallback(async () => {
    if (!selectedCountry) return;
    setLoading(true);
    try {
      const res = await api.get(`/holidays/country/${selectedCountry.id}/year/${yearFilter}`);
      setHolidays(res.holidays || []);
    } catch (err) {
      setHolidays([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCountry, yearFilter]);

  useEffect(() => {
    fetchCountries();
    // Fetch the observed holiday cap from the server
    api.get('/holidays/limits')
      .then(res => { if (res.max_observed_holidays) setMaxObserved(res.max_observed_holidays); })
      .catch(() => {}); // Use default if fetch fails
  }, [fetchCountries]);

  useEffect(() => {
    fetchHolidays();
  }, [fetchHolidays]);

  const showFeedback = (message, type = 'success') => {
    setFeedback({ message, type });
    setTimeout(() => setFeedback(null), 4000);
  };

  const observedCount = holidays.filter(h => h.is_observed).length;
  const atObservedLimit = observedCount >= maxObserved;

  const handleToggleObserved = async (holidayId, isObserved) => {
    // Client-side check before hitting the server
    if (isObserved && atObservedLimit) {
      showFeedback(`Maximum of ${maxObserved} observed holidays per country. Please un-observe another holiday first.`, 'error');
      return;
    }
    try {
      await api.put(`/holidays/${holidayId}/observed`, { is_observed: isObserved });
      fetchHolidays();
    } catch (err) {
      showFeedback(err.message || 'Failed to toggle observed status', 'error');
    }
  };

  const handlePopulate = async () => {
    if (!selectedCountry) return;
    setPopulateLoading(true);
    try {
      const res = await api.post(`/holidays/country/${selectedCountry.id}/populate`);
      showFeedback(`Populated ${res.created} holidays${res.skipped > 0 ? ` (${res.skipped} already existed)` : ''}`);
      fetchHolidays();
    } catch (err) {
      showFeedback(err.message || 'Failed to populate holidays', 'error');
    } finally {
      setPopulateLoading(false);
    }
  };

  const handleGenerateDates = async () => {
    if (!selectedCountry) return;
    setGenerateLoading(true);
    try {
      const res = await api.post(`/holidays/country/${selectedCountry.id}/generate-dates/${yearFilter}`);
      const generated = res.results?.filter(r => r.date).length || 0;
      showFeedback(`Generated dates for ${generated} holidays in ${yearFilter}`);
      fetchHolidays();
    } catch (err) {
      showFeedback(err.message || 'Failed to generate dates', 'error');
    } finally {
      setGenerateLoading(false);
    }
  };

  const handleAcceptShift = async (dateId) => {
    try {
      await api.post(`/holidays/dates/${dateId}/accept-shift`);
      fetchHolidays();
      showFeedback('Weekend shift accepted');
    } catch (err) {
      showFeedback('Failed to accept shift', 'error');
    }
  };

  const handleOverrideDate = async (dateId, data) => {
    try {
      await api.put(`/holidays/dates/${dateId}`, data);
      fetchHolidays();
      showFeedback('Holiday date updated');
    } catch (err) {
      showFeedback('Failed to update date', 'error');
    }
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    try {
      let variableRule = null;
      if (formData.recurring_type === 'variable' && formData.variable_rule) {
        try {
          variableRule = JSON.parse(formData.variable_rule);
        } catch {
          showFeedback('Invalid variable rule JSON', 'error');
          return;
        }
      }

      const payload = {
        country_id: selectedCountry.id,
        name: formData.name,
        description: formData.description,
        recurring_type: formData.recurring_type,
        fixed_month: formData.recurring_type === 'fixed' ? parseInt(formData.fixed_month) || null : null,
        fixed_day: formData.recurring_type === 'fixed' ? parseInt(formData.fixed_day) || null : null,
        variable_rule: variableRule,
        is_observed: formData.is_observed,
        is_recurring: formData.is_recurring,
      };

      if (editingHoliday) {
        await api.put(`/holidays/${editingHoliday.id}`, payload);
        showFeedback('Holiday updated');
      } else {
        await api.post('/holidays', payload);
        showFeedback('Holiday created');
      }

      resetForm();
      fetchHolidays();
    } catch (err) {
      showFeedback(err.message || 'Failed to save holiday', 'error');
    }
  };

  const handleEdit = (holiday) => {
    setEditingHoliday(holiday);
    setFormData({
      name: holiday.name,
      description: holiday.description || '',
      recurring_type: holiday.recurring_type,
      fixed_month: holiday.fixed_month?.toString() || '',
      fixed_day: holiday.fixed_day?.toString() || '',
      variable_rule: holiday.variable_rule ? JSON.stringify(holiday.variable_rule, null, 2) : '',
      is_observed: holiday.is_observed,
      is_recurring: holiday.is_recurring,
    });
    setShowAddForm(true);
  };

  const handleDelete = async (holiday) => {
    try {
      await api.delete(`/holidays/${holiday.id}`);
      showFeedback(`"${holiday.name}" deleted`);
      setConfirmDelete(null);
      fetchHolidays();
    } catch (err) {
      showFeedback('Failed to delete holiday', 'error');
    }
  };

  const resetForm = () => {
    setShowAddForm(false);
    setEditingHoliday(null);
    setFormData({
      name: '',
      description: '',
      recurring_type: 'fixed',
      fixed_month: '',
      fixed_day: '',
      variable_rule: '',
      is_observed: true,
      is_recurring: true,
    });
  };

  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2];

  return (
    <div className="holidays-page">
      <div className="holidays-header">
        <div className="holidays-header-left">
          <Globe size={22} strokeWidth={2} />
          <h1>Holiday Management</h1>
        </div>
      </div>

      {feedback && (
        <div className={`holidays-feedback ${feedback.type}`}>
          {feedback.message}
        </div>
      )}

      {/* Country Tabs */}
      <div className="country-tabs">
        {countries.map(c => (
          <button
            key={c.id}
            className={`country-tab ${selectedCountry?.id === c.id ? 'active' : ''}`}
            onClick={() => setSelectedCountry(c)}
          >
            <CountryFlag code={c.code} size="small" />
            <span className="country-tab-code">{c.code}</span>
            <span className="country-tab-name">{c.name}</span>
            <span className={`country-tab-count${(c.observed_holiday_count || 0) >= maxObserved ? ' at-limit' : ''}`}>
              {c.observed_holiday_count || 0}/{maxObserved}
            </span>
          </button>
        ))}
      </div>

      {selectedCountry && (
        <>
          {/* Toolbar */}
          <div className="holidays-toolbar">
            <div className="toolbar-left">
              <select
                className="year-select"
                value={yearFilter}
                onChange={(e) => setYearFilter(parseInt(e.target.value))}
              >
                {yearOptions.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <button
                className="toolbar-btn secondary"
                onClick={handleGenerateDates}
                disabled={generateLoading}
              >
                <RefreshCw size={14} className={generateLoading ? 'spin' : ''} />
                {generateLoading ? 'Generating...' : 'Generate Dates'}
              </button>
            </div>
            <div className="toolbar-right">
              <button
                className="toolbar-btn secondary"
                onClick={handlePopulate}
                disabled={populateLoading}
              >
                <Sparkles size={14} />
                {populateLoading ? 'Populating...' : 'Populate Holidays'}
              </button>
              <button className="toolbar-btn primary" onClick={() => setShowAddForm(true)}>
                <Plus size={14} />
                Add Holiday
              </button>
            </div>
          </div>

          {/* Observed holiday cap indicator */}
          <div className={`holiday-cap-banner${atObservedLimit ? ' at-limit' : ''}`}>
            <span>
              <strong>{observedCount}</strong> of <strong>{maxObserved}</strong> holidays observed for {selectedCountry.name}.
              {atObservedLimit
                ? ' Limit reached — un-observe a holiday before adding another.'
                : ` You can observe ${maxObserved - observedCount} more.`
              }
            </span>
          </div>

          {/* Add/Edit Form */}
          {showAddForm && (
            <div className="holiday-form-container">
              <div className="holiday-form-header">
                <h3>{editingHoliday ? 'Edit Holiday' : 'Add Custom Holiday'}</h3>
                <button className="icon-btn" onClick={resetForm}>
                  <X size={16} />
                </button>
              </div>
              <form className="holiday-form" onSubmit={handleSubmitForm}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))}
                      required
                      placeholder="Holiday name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Type</label>
                    <select
                      value={formData.recurring_type}
                      onChange={(e) => setFormData(f => ({ ...f, recurring_type: e.target.value }))}
                    >
                      <option value="fixed">Fixed (same date every year)</option>
                      <option value="variable">Variable (rule-based)</option>
                      <option value="custom">Custom (one-time or admin-defined)</option>
                    </select>
                  </div>
                </div>

                {formData.recurring_type === 'fixed' && (
                  <div className="form-row">
                    <div className="form-group">
                      <label>Month</label>
                      <select
                        value={formData.fixed_month}
                        onChange={(e) => setFormData(f => ({ ...f, fixed_month: e.target.value }))}
                        required
                      >
                        <option value="">Select month</option>
                        {Array.from({ length: 12 }, (_, i) => (
                          <option key={i + 1} value={i + 1}>
                            {new Date(2000, i, 1).toLocaleDateString('en-US', { month: 'long' })}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Day</label>
                      <input
                        type="number"
                        min="1"
                        max="31"
                        value={formData.fixed_day}
                        onChange={(e) => setFormData(f => ({ ...f, fixed_day: e.target.value }))}
                        required
                        placeholder="Day"
                      />
                    </div>
                  </div>
                )}

                {formData.recurring_type === 'variable' && (
                  <div className="form-group">
                    <label>Variable Rule (JSON)</label>
                    <textarea
                      value={formData.variable_rule}
                      onChange={(e) => setFormData(f => ({ ...f, variable_rule: e.target.value }))}
                      placeholder='e.g., {"week": 3, "weekday": 1, "month": 11}'
                      rows={3}
                    />
                    <span className="form-hint">
                      Standard: {'{'}week, weekday (0=Sun..6=Sat), month{'}'} | Easter: {'{'}type: "easter_offset", offset{'}'} | Next Monday: {'{'}type: "next_monday", reference_month, reference_day{'}'}
                    </span>
                  </div>
                )}

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(f => ({ ...f, description: e.target.value }))}
                    placeholder="Cultural description of the holiday"
                    rows={3}
                  />
                </div>

                <div className="form-row">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.is_observed}
                      onChange={(e) => setFormData(f => ({ ...f, is_observed: e.target.checked }))}
                    />
                    Observed by FPS
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.is_recurring}
                      onChange={(e) => setFormData(f => ({ ...f, is_recurring: e.target.checked }))}
                    />
                    Recurring yearly
                  </label>
                </div>

                <div className="form-actions">
                  <button type="button" className="btn-cancel" onClick={resetForm}>Cancel</button>
                  <button type="submit" className="btn-primary">
                    {editingHoliday ? 'Update Holiday' : 'Add Holiday'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Holiday List */}
          <div className="holidays-list">
            {loading ? (
              <div className="holidays-loading">Loading holidays...</div>
            ) : holidays.length === 0 ? (
              <div className="holidays-empty">
                <Globe size={40} strokeWidth={1.5} />
                <p>No holidays found for {selectedCountry.name}.</p>
                <p className="holidays-empty-hint">
                  Use "Populate Holidays" to auto-fill national holidays, or add custom ones manually.
                </p>
              </div>
            ) : (
              holidays.map(h => (
                <HolidayCard
                  key={h.id}
                  holiday={h}
                  onToggleObserved={handleToggleObserved}
                  onEdit={handleEdit}
                  onDelete={(hol) => setConfirmDelete(hol)}
                  onAcceptShift={handleAcceptShift}
                  onOverrideDate={handleOverrideDate}
                  canManage={true}
                  isAdmin={isAdmin}
                  observeLimitReached={atObservedLimit}
                />
              ))
            )}
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div className="modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Holiday</h3>
            <p>Are you sure you want to delete <strong>{confirmDelete.name}</strong>? This will also remove all generated dates for this holiday.</p>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="btn-danger" onClick={() => handleDelete(confirmDelete)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Holidays;
